
import { Form } from "element-ui";
import { Vue, Component, Prop, Watch, Ref } from "vue-property-decorator";
import { BgFormItem, BgDialog } from "@banggood/scm-fe";
import ImageUploader from "@/components/image-uploader/index.vue";
import cloneDeep from "lodash/cloneDeep";
import { scrollElementFormErrorIntoView } from "@/utils";
import { getNewProductFeatureLabels } from "@/service/ewold/api/newProduct";
import { NewProductFeatureLabel } from "@/service/ewold/entity/NewProductFeatureLabel";
import debounce from "lodash/debounce";
import { View, Wrapper } from "vue-virtual-scroller";
import { FormModel, FormField } from "@/service/common/form/FormModel";
import FormItem from "@banggood/scm-fe/lib/packages/form-item";
import IconError from "./icon-error.vue";
export enum POAItemSourceEnum {
  /** 本地生成 */
  local = 1,
  /** 阿里1688 */
  ali = 2,
}
export interface POAForm {
  items: POAItem[];
}
export interface POAItem {
  key?: string;
  images: string[];
  color: string;
  size: string;
  sizeType?: number;
  featureLabels: NewProductFeatureLabelOption[];
  price: string;
  volume: Volume;
  weight: string;
  arrivalDays: string;
  minOrderQuantity: string;
}
export interface SKUSpecItem {
  featureLabels: number[];
  price: string;
  weight: string;
  arrivalDays: string;
  minOrderQuantity: string;
  volume: Volume;
}
interface Volume {
  length: string;
  width: string;
  height: string;
}
interface NewProductFeatureLabelOption extends NewProductFeatureLabel {
  label: string;
  value: number;
  children?: {
    label: string;
    value: number;
    isLeaf: boolean;
  }[];
}
const decimalValidator = (...args: any[]) => {
  const value = args[1] as string;
  console.log("decimalValidator value", value);
  const callback = args[2] as (msg?: string) => void;
  const reg = /^\d+(\.\d{1,2})?$/;
  if (!reg.test(value)) {
    callback(`请输入有效数值，保留2位小数`);
  } else {
    callback();
  }
};
const intValidator = (...args: any[]) => {
  const value = args[1] as string;
  const callback = args[2] as (msg?: string) => void;
  const reg = /^\d+$/;
  if (!reg.test(value)) {
    callback(`请输入正整数`);
  } else {
    callback();
  }
};
function genPoaItemKey(color: string, size: string) {
  return `${color}_${size}_${new Date().getTime()}`;
}
@Component({
  components: {
    BgFormItem,
    ImageUploader,
    BgDialog,
    IconError,
  },
})
export default class POAEditor extends Vue {
  @Ref("skuSpecForm") elSkuSpecForm!: Form;
  form = {
    items: [] as POAItem[],
  } as POAForm;
  poaFormModel = new FormModel();
  skuSpecForm = {
    items: [
      {
        featureLabels: [],
        price: "",
        weight: "",
        arrivalDays: "",
        minOrderQuantity: "",
        volume: {
          length: "",
          width: "",
          height: "",
        },
      },
    ] as SKUSpecItem[],
  };
  imgUploaderDialogState = {
    visible: false,
    images: [] as string[],
    itemIndex: -1,
  };
  poaFeatureLabelDialogState = {
    visible: false,
    item: null as null | POAItem,
    value: [] as number[],
  };
  newProductFeatureLabelOptions: NewProductFeatureLabelOption[] = [];
  featureLabelCascaderProps = {
    multiple: true,
    label: "label",
    value: "value",
    children: "children",
    emitPath: false,
  };
  featureLabelMap: Map<number, string> = new Map();
  /** 是否有校验过 */
  validated = false;
  get skuSpecRules() {
    return {
      price: [
        {
          required: this.skuSpecFormVisible,
          message: "请填写价格",
          trigger: "blur",
        },
        {
          validator: decimalValidator,
          trigger: "blur",
        },
      ],
      weight: [
        {
          required: this.skuSpecFormVisible,
          message: "请填写重量",
          trigger: "blur",
        },
        {
          validator: decimalValidator,
          trigger: "blur",
        },
      ],
      arrivalDays: [
        {
          required: this.skuSpecFormVisible,
          message: "请填写到货/生产天数",
          trigger: "blur",
        },
        {
          validator: intValidator,
          trigger: "blur",
        },
      ],
      minOrderQuantity: [
        {
          required: this.skuSpecFormVisible,
          message: "请填写最小起订量",
          trigger: "blur",
        },
        {
          validator: intValidator,
          trigger: "blur",
        },
      ],
    };
  }
  get hasPoaItem() {
    return this.form.items.length > 0;
  }
  get poaFormVisible() {
    return this.hasPoaItem;
  }
  get skuSpecFormVisible() {
    return !this.hasPoaItem;
  }
  @Watch("form.items.length", { immediate: true })
  handleItemsLengthChange(length: number) {
    this.$emit("POALengthChange", length);
  }
  @Watch("form.items", { immediate: true, deep: true })
  handleFormItemsChange(val: POAItem[]) {
    if (val.length === 0) {
      this.poaFormModel.setFields({});
      return;
    }
    const fields = val
      .map((item, index) => this.genPoaItemFormFields(item, index))
      .flat();
    console.log("handleFormItemsChange", fields);
    this.poaFormModel.updateFields(fields);
    console.log("poaFormModel", this.poaFormModel.getFields());
  }
  genPoaItemFormFields(item: POAItem, index: number): FormField[] {
    const rules = {
      [`items.${index}.images`]: [
        {
          required: true,
          message: "请上传图片",
        },
      ],
      [`items.${index}.price`]: [
        {
          required: this.poaFormVisible,
          message: "请填写价格",
        },
        {
          validator: decimalValidator,
        },
      ],
      [`items.${index}.volume`]: [
        {
          validator: (...args: unknown[]) => {
            const value = args[1] as Volume;
            const callback = args[2] as (msg?: string) => void;
            if (!value.length || !value.width || !value.height) {
              callback("请输入最长边/次长边/最短边");
            } else {
              const floatReg = /^\d+(\.\d+)?$/;
              const errAttrNames: string[] = [];
              if (!floatReg.test(value.length)) {
                errAttrNames.push("最长边");
              }
              if (!floatReg.test(value.width)) {
                errAttrNames.push("次长边");
              }
              if (!floatReg.test(value.height)) {
                errAttrNames.push("最短边");
              }
              if (errAttrNames.length) {
                callback(`${errAttrNames.join("，")}输入有误，请输入有效数值`);
              } else {
                callback();
              }
            }
          },
        },
      ],
      [`items.${index}.weight`]: [
        {
          required: this.poaFormVisible,
          message: "请填写重量",
        },
        {
          validator: decimalValidator,
        },
      ],
      [`items.${index}.arrivalDays`]: [
        {
          required: this.poaFormVisible,
          message: "请填写到货/生产天数",
          trigger: "blur",
        },
        {
          validator: intValidator,
          trigger: "blur",
        },
      ],
      [`items.${index}.minOrderQuantity`]: [
        {
          required: this.poaFormVisible,
          message: "请填写最小起订量",
          trigger: "blur",
        },
        {
          validator: intValidator,
          trigger: "blur",
        },
      ],
    };
    const res: FormField[] = [];
    Object.entries(rules).forEach(([prop, rules]) => {
      res.push({
        prop,
        rules,
      });
    });
    return res;
  }
  created() {
    this.poaFormModel.setModel(this.form);
  }
  mounted() {
    getNewProductFeatureLabels().then((res) => {
      if (Array.isArray(res.result)) {
        this.newProductFeatureLabelOptions = res.result.map((ele) => ({
          ...ele,
          label: ele.category_name,
          value: ele.category_id,
          children: ele.label_items.map((item) => ({
            label: item.label_name,
            value: item.label_id,
            isLeaf: true,
          })),
        }));
      }
    });
  }
  itemRules(index: number) {
    return {
      images: [
        {
          required: true,
          message: "请上传图片",
          trigger: "change",
        },
      ],
      price: [
        {
          required: this.poaFormVisible,
          message: "请填写价格",
          trigger: "blur",
        },
        {
          validator: decimalValidator,
          trigger: "blur",
        },
      ],
      volume: [
        {
          validator: (...args: unknown[]) => {
            const value = args[1] as Volume;
            const callback = args[2] as (msg?: string) => void;
            if (!value.length || !value.width || !value.height) {
              callback("请输入最长边/次长边/最短边");
            } else {
              const floatReg = /^\d+(\.\d+)?$/;
              const errAttrNames: string[] = [];
              if (!floatReg.test(value.length)) {
                errAttrNames.push("最长边");
              }
              if (!floatReg.test(value.width)) {
                errAttrNames.push("次长边");
              }
              if (!floatReg.test(value.height)) {
                errAttrNames.push("最短边");
              }
              if (errAttrNames.length) {
                callback(`${errAttrNames.join("，")}输入有误，请输入有效数值`);
              } else {
                callback();
              }
            }
          },
        },
      ],
      weight: [
        {
          required: this.poaFormVisible,
          message: "请填写重量",
          trigger: "blur",
        },
        {
          validator: decimalValidator,
          trigger: "blur",
        },
      ],
      arrivalDays: [
        {
          required: this.poaFormVisible,
          message: "请填写到货/生产天数",
          trigger: "blur",
        },
        {
          validator: intValidator,
          trigger: "blur",
        },
      ],
      minOrderQuantity: [
        {
          required: this.poaFormVisible,
          message: "请填写最小起订量",
          trigger: "blur",
        },
        {
          validator: intValidator,
          trigger: "blur",
        },
      ],
    };
  }
  getItemThumb(item: POAItem) {
    if (Array.isArray(item.images) && item.images.length) {
      return item.images[0];
    }
    return "";
  }
  editItemImages(item: POAItem, index: number) {
    this.imgUploaderDialogState.images = [...item.images];
    this.imgUploaderDialogState.visible = true;
    this.imgUploaderDialogState.itemIndex = index;
  }
  removeItem(index: number) {
    if (this.form.items[index]) {
      this.poaFormModel.clearValidate([
        `items.${index}.images`,
        `items.${index}.price`,
        `items.${index}.volume`,
        `items.${index}.weight`,
        `items.${index}.arrivalDays`,
        `items.${index}.minOrderQuantity`,
      ]);
    }

    this.form.items.splice(index, 1);
    setTimeout(() => {
      if (this.form.items[index]) {
        this.poaFormModel.validateFields([
          `items.${index}.images`,
          `items.${index}.price`,
          `items.${index}.volume`,
          `items.${index}.weight`,
          `items.${index}.arrivalDays`,
          `items.${index}.minOrderQuantity`,
        ]);
      }
    });
  }
  setPoaItems(items: POAItem[]) {
    // 将已有的且相同的item排在前面
    console.log("items", items);
    const existItems: POAItem[] = [];
    const newItems: POAItem[] = [];
    this.form.items.forEach((item) => {
      if (items.some((i) => i.color === item.color && i.size === item.size)) {
        if (!item.key) {
          item.key = genPoaItemKey(item.color, item.size);
        }
        existItems.push(item);
      }
    });
    items.forEach((item) => {
      const sameItem = this.form.items.find(
        (i) => i.color === item.color && i.size === item.size
      );
      if (!sameItem) {
        if (!item.key) {
          item.key = genPoaItemKey(item.color, item.size);
        }
        newItems.push(item);
      }
    });
    this.form.items = existItems.concat(newItems);
    if (this.form.items.length === 0) {
      this.validated = false;
    }
  }
  oneKeySet(field: string) {
    if (this.form.items.length < 2) {
      return;
    }
    const templateItem = this.form.items[0];
    for (let i = 1; i < this.form.items.length; i++) {
      // eslint-disable-next-line no-prototype-builtins
      if (!this.form.items[i].hasOwnProperty(field)) {
        return;
      }
      if (field === "volume") {
        this.form.items[i].volume = {
          ...templateItem[field],
        };
      } else {
        const key = field as keyof typeof templateItem;
        Object.assign(this.form.items[i], {
          [key]: templateItem[key],
        });
      }
    }
  }
  handleImageUploaderChange(urls: string[]) {
    this.form.items[this.imgUploaderDialogState.itemIndex].images = urls;
    this.poaFormModel.validateField(
      `items.${this.imgUploaderDialogState.itemIndex}.images`
    );
  }
  editPoaFeatureLabel(item: POAItem) {
    this.poaFeatureLabelDialogState.item = item;
    this.poaFeatureLabelDialogState.value = (item.featureLabels || []).map(
      (ele) => ele.value
    );
    this.poaFeatureLabelDialogState.visible = true;
  }
  savePoaFeatureLabel() {
    if (this.poaFeatureLabelDialogState.item) {
      const checkedNodes = (
        this.$refs.newProductFeatureLabelCascaderPanel as any
      ).getCheckedNodes();

      this.poaFeatureLabelDialogState.item.featureLabels = (
        checkedNodes || []
      ).map(
        (ele: { label: string; value: number }) =>
          ({
            label: ele.label,
            value: ele.value,
          } as unknown as NewProductFeatureLabelOption)
      );
    }
    this.poaFeatureLabelDialogState.visible = false;
  }
  cancelEditPoaFeatureLabel() {
    this.poaFeatureLabelDialogState.visible = false;
    this.poaFeatureLabelDialogState.item = null;
    this.poaFeatureLabelDialogState.value = [];
  }
  async validate(): Promise<boolean> {
    this.validated = true;
    if (this.poaFormVisible) {
      const res = await this.poaFormModel.validate().catch(() => false);
      return res;
    } else {
      return await this.elSkuSpecForm.validate().catch(() => false);
    }
  }
  scrollErrorIntoView() {
    if (this.poaFormVisible) {
      // scrollElementFormErrorIntoView(this.elForm);
      // this.elForm.$el.scrollIntoView();
    } else {
      scrollElementFormErrorIntoView(this.elSkuSpecForm);
    }
  }
  getPoaItems() {
    return cloneDeep(this.form.items);
  }
  getSKUSpec() {
    return cloneDeep(this.skuSpecForm.items[0]);
  }
  genFeatureLabelMap() {
    this.newProductFeatureLabelOptions.forEach((ele) => {
      ele.children?.forEach((child) => {
        this.featureLabelMap.set(child.value, child.label);
      });
    });
  }
  getFeatureLabelNameById(id: number) {
    if (this.featureLabelMap.size === 0) {
      this.genFeatureLabelMap();
    }
    return this.featureLabelMap.get(id) || "";
  }
  setSkuSpecForm(data: { items: SKUSpecItem[] }) {
    this.skuSpecForm = data;
  }
}
